import React, { createRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import useStyles from './LabRequestsList.styles';
import { selectListError, openCreateAuditTrail, closeCreateAuditTrail, createAuditTrail, selectPaging, loadLabRequests, filter } from '../../state';
import events from './events';
import MaterialTable, { Column, MaterialTableProps, MTableToolbar, Query } from 'material-table';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import CreateAuditTrailEvent from '../../components/CreateAuditTrailEvent';
import AuditTrailCell from '../../components/AuditTrailCell';
import AuditTrailFilters from '../../components/AuditTrailFilters';
import { useLastLocation } from 'react-router-last-location';
import { LabRequest } from '../../../../interfaces/lab-request';

const fixed = (left: number): CSSProperties => ({
  position: 'sticky',
  backgroundColor: 'white',
  left,
  overflow: 'hidden',
  wordWrap: 'break-word'
});

const camelCase = (input: string) => input
  .toLowerCase()
  .replace(/-(.)/g, (match, group1) => group1.toUpperCase());

let scrollPosition: any = null;

export default function LabRequestsList() {
  const error = useSelector(selectListError);
  const classes = useStyles();
  const dispatch = useDispatch();
  const paging = useSelector(selectPaging);
  const lastLocation = useLastLocation();
  const history = useHistory();
  const tableRef = createRef<MaterialTableProps<LabRequest>>();

  if (error) {
    // TODO: implement correctly with retry operation
    return (
      <div className={classes.error}>
        {error}
      </div>
    )
  }

  const columns: Column<LabRequest>[] = [
    { field: 'userId', title: 'User ID', width: 160, minWidth: 160,
      cellStyle: {...fixed(0), minWidth: 160, maxWidth: 160, zIndex: 10} as CSSProperties,
      headerStyle: {...fixed(0), zIndex: 11} as CSSProperties,
      render: (data: LabRequest) => (<Link to={`/users/${data.userId}`}>{data.userId}</Link>)
    },
    { field: 'product', title: 'Subscription', width: 160, minWidth: 160,
      cellStyle: {...fixed(160), minWidth: 160, maxWidth: 160, zIndex: 10} as CSSProperties,
      headerStyle: {...fixed(160), zIndex: 11} as CSSProperties
    },
    { field: 'id', title: 'Sample ID', width: 160, minWidth: 160, sorting: false,
      cellStyle: {...fixed(320), minWidth: 160, maxWidth: 160, zIndex: 10} as CSSProperties,
      headerStyle: {...fixed(320), zIndex: 11} as CSSProperties,
      render: (data: LabRequest) => (<Link to={`/lab-requests/${data.id}`}>{data.id}</Link>)
    },
    { field: 'labResultIds', title: 'Sample ID (Eurofins)', width: 160, minWidth: 160, sorting: false,
      cellStyle: {...fixed(480), minWidth: 160, maxWidth: 160, zIndex: 10, borderRight: '1px solid black'} as CSSProperties,
      headerStyle: {...fixed(480), zIndex: 11, borderRight: '1px solid black'} as CSSProperties,
      render: (data: LabRequest) => (
        <ul>
          {(data.labResultIds || [])
            .map((id) => (<li key={id}>{id.split('-')[1]}</li>))}
        </ul>
      )
    },
    { field: 'test', title: 'Panel ID', width: 'auto', minWidth: 'auto' },
    { field: 'requestedTestsLabel', title: 'Eurofins Test ID', width: 140, minWidth: 140 },
    { field: 'accessCode', title: 'Access Code', width: 140, minWidth: 140, sorting: false }
  ].concat(events.map((event) => ({
    title: event.title,
    field: `${camelCase(event.code)}Timestamp`,
    sorting: event.sort,
    render: (data: any) => {
      return <AuditTrailCell
        date={data[`${camelCase(event.code)}Timestamp`]}
        metadata={data[`${camelCase(event.code)}Metadata`]}
        events={data[`${camelCase(event.code)}Count`]}
        onAdd={() => dispatch(openCreateAuditTrail({code: event.code, labRequestId: data.id}))}
      />
    },
    width: 250,
    minWidth: 250,
    cellStyle: {minWidth: 250},
    headerStyle: {minWidth: 250, textAlign: 'center'}
  })));

  const currentSortColumn = columns.find(({field}) => field === paging.sortField);
  if (currentSortColumn) {
    currentSortColumn.defaultSort = paging.sortType;
  }

  const backFromDetails = lastLocation && lastLocation.pathname.includes('/lab-requests/');

  return (
    <div className="audit-trail">
      <MaterialTable
        title='Audit Trail'
        tableRef={tableRef}
        columns={columns}
        data={(query) => {
          // backFromDetails is required to come around a bug with initialPage.
          const page = backFromDetails ? paging.page : query.page;
          return (dispatch(loadLabRequests({
            ...paging,
            page,
            pageSize: query.pageSize,
            sortField: query.orderBy.field,
            sortType: query.orderDirection,
            search: query.search
          })) as any)
            .then((data: any[]) => {
              if (backFromDetails) {
                history.push(`/audit-trail?page=${page}`);
              }
              if (scrollPosition) {
                const container = document.getElementById('audit-scroll-container')?.children[1]?.children[0]?.children[0];
                container?.scrollTo(scrollPosition);
                scrollPosition = null;
              }
              return {
                data: data.map(o => ({ ...o })),
                page,
                totalCount: data.length === query.pageSize ? Infinity : (query.pageSize * page) + data.length
              };
            })
        }}
        components={{
          Container: props => (
            <div id='audit-scroll-container' style={{height: 'calc(100vh - 64px)'}}>
              {props.children}
            </div>
          ),
          Toolbar: props => (
            <div>
              <MTableToolbar {...props} />
              <AuditTrailFilters onChange={({roaPassed, genotypingPass}: {roaPassed: string, genotypingPass: string}) => {
                if (tableRef?.current && tableRef?.current.onQueryChange) {
                  dispatch(filter({
                    roaPassed: roaPassed ? roaPassed === 'true' : '',
                    genotypingPass: genotypingPass ? genotypingPass === 'true' : '',
                  }));
                  tableRef?.current.onQueryChange({} as Query<LabRequest>);
                }
              }
              } />
            </div>
          ),
        }}
        localization={{
          pagination: {
            labelDisplayedRows: '{from}-{to}',
          },
        }}
        options={{
          actionsCellStyle: {...fixed(0), minWidth: 58, zIndex: 10},
          headerStyle: { position: 'sticky', top: 0 },
          maxBodyHeight: 'calc(100vh - 192px - 53px)',
          paging: true,
          thirdSortClick: false,
          initialPage: 0,
          pageSize: paging.pageSize,
          searchText: paging.search,
          pageSizeOptions: [15, 30, 45],
          paginationType: 'normal',
          showFirstLastPageButtons: false,
          emptyRowsWhenPaging: false,
          debounceInterval: 500,
        }}
      />
      <CreateAuditTrailEvent
        onClose={() => dispatch(closeCreateAuditTrail())}
        onSave={(data: {metadata: any, timestamp: string}) => {
          const container = document.getElementById('audit-scroll-container')?.children[1]?.children[0]?.children[0];
          scrollPosition = {left: container?.scrollLeft, top: container?.scrollTop};
          (dispatch(createAuditTrail(data)) as any)
            .then(() => {
              if (tableRef?.current && tableRef?.current.onQueryChange) {
                tableRef?.current.onQueryChange({} as Query<LabRequest>);
              }
            })
        }}
      />
    </div>
  );
}
